/**
 * Demo Portal — guided synthetic Trust Verification workflow.
 *
 * Walks a prospect through the seven canonical steps end-to-end using
 * only the seeded demo dataset (100000017, 200000016).
 * Every mutation goes through the SAME backend guards as production —
 * the only difference is the three-key sandbox gate that permits
 * seeding/resetting the synthetic corpus.
 */
import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ChevronRight, RefreshCw, Play, Loader2 } from "lucide-react";

const DEMO_IDENTITY = "100000017";
const DEMO_INSTITUTION = "200000016";
const DEMO_CITIZEN_EMAIL = "citizen-a@veralok.io";

const STEPS = [
  { id: 1, label: "Institution Login", key: "login" },
  { id: 2, label: "QR Verification", key: "qr" },
  { id: 3, label: "Consent Request", key: "consent" },
  { id: 4, label: "Citizen Approve / Deny", key: "decide" },
  { id: 5, label: "Trust Event", key: "trust" },
  { id: 6, label: "Audit Trail", key: "audit" },
  { id: 7, label: "Analytics", key: "analytics" },
];

export default function DemoWorkflow() {
  const [step, setStep] = useState(1);
  const [state, setState] = useState({
    identityId: DEMO_IDENTITY,
    institutionId: DEMO_INSTITUTION,
    qr: null,
    qrVerify: null,
    consent: null,
    decision: null,
    trustEvent: null,
    audit: [],
    analytics: null,
  });
  const [busy, setBusy] = useState(false);
  const [seedInfo, setSeedInfo] = useState(null);

  // Ensure demo seed is present on mount
  useEffect(() => {
    api.post("/consent/demo/seed?force_reseed=false")
      .then((r) => setSeedInfo(r.data))
      .catch((e) => {
        if (e?.response?.status === 403) {
          setSeedInfo({ refused: true, message: e.response.data?.detail?.message });
        }
      });
  }, []);

  const advance = (nextStep, patch = {}) => {
    setState((s) => ({ ...s, ...patch }));
    setStep(nextStep);
  };

  const reset = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/consent/demo/reset", {});
      await api.post("/consent/demo/seed?force_reseed=true", {});
      setStep(1);
      setState({
        identityId: DEMO_IDENTITY,
        institutionId: DEMO_INSTITUTION,
        qr: null, qrVerify: null, consent: null,
        decision: null, trustEvent: null, audit: [], analytics: null,
      });
      toast.success("Demo dataset reset");
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail?.message || e.message });
    } finally { setBusy(false); }
  }, []);

  return (
    <div className="space-y-6" data-testid="demo-workflow">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Guided Trust Verification Workflow</h1>
          <p className="text-sm text-muted-foreground">
            A synthetic end-to-end tour of a VeraLok verification — from QR scan to Trust Event to audit + analytics.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reset} disabled={busy}
                data-testid="demo-reset-btn">
          <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Reset demo data
        </Button>
      </div>

      {seedInfo?.refused && (
        <div className="border-2 border-destructive/40 bg-destructive/5 rounded p-4 text-xs">
          <strong>Demo endpoints refused by backend:</strong> {seedInfo.message}. The Demo Portal is
          only available inside a strictly sandboxed environment — production writes are never possible.
        </div>
      )}

      <StepStrip step={step} />

      <div className="border rounded p-6 space-y-4" data-testid={`demo-step-body-${step}`}>
        {step === 1 && <Step1Login onDone={() => advance(2)} />}
        {step === 2 && <Step2QR state={state} setBusy={setBusy} busy={busy} onDone={(patch) => advance(3, patch)} />}
        {step === 3 && <Step3Consent state={state} setBusy={setBusy} busy={busy} onDone={(patch) => advance(4, patch)} />}
        {step === 4 && <Step4Decide state={state} setBusy={setBusy} busy={busy} onDone={(patch) => advance(5, patch)} />}
        {step === 5 && <Step5TrustEvent state={state} setBusy={setBusy} busy={busy} onDone={(patch) => advance(6, patch)} />}
        {step === 6 && <Step6Audit state={state} setBusy={setBusy} busy={busy} onDone={(patch) => advance(7, patch)} />}
        {step === 7 && <Step7Analytics state={state} setBusy={setBusy} busy={busy} />}
      </div>

      <details className="border rounded p-3">
        <summary className="cursor-pointer text-xs uppercase tracking-widest text-muted-foreground">Workflow state</summary>
        <pre className="text-[10px] font-mono mt-2 max-h-64 overflow-auto whitespace-pre-wrap">
          {JSON.stringify(state, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function StepStrip({ step }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto" data-testid="demo-step-strip">
      {STEPS.map((s, i) => {
        const done = step > s.id;
        const active = step === s.id;
        return (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded text-xs ${
              done ? "bg-green-500/10 text-green-700 dark:text-green-500" :
              active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}
            data-testid={`demo-step-${s.id}-${done ? "done" : active ? "active" : "idle"}`}>
              {done ? <Check className="h-3 w-3" /> : <span className="font-mono">{s.id}</span>}
              <span className="whitespace-nowrap">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Institution Login (already logged in via demo user) ───────────
function Step1Login({ onDone }) {
  return (
    <div className="space-y-3" data-testid="demo-step-login">
      <div className="text-sm">
        You are viewing the Demo Portal as an authenticated user. In production, an institution
        admin signs in with tenant-scoped credentials and can only ever see verification events
        bound to their own institution — strict data isolation is enforced at the platform level.
      </div>
      <div className="border rounded p-3 text-xs">
        <div className="uppercase tracking-widest text-muted-foreground mb-1">Demo identity</div>
        <div className="font-mono">Institution: {DEMO_INSTITUTION}</div>
        <div className="font-mono">Citizen: {DEMO_IDENTITY} ({DEMO_CITIZEN_EMAIL})</div>
      </div>
      <Button onClick={onDone} data-testid="demo-step1-continue">
        <Play className="h-3 w-3 mr-1" /> Begin verification
      </Button>
    </div>
  );
}

// ─── Step 2: QR create + verify ────────────────────────────────────────────
function Step2QR({ state, setBusy, busy, onDone }) {
  const [qr, setQr] = useState(state.qr);
  const [verify, setVerify] = useState(state.qrVerify);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/qr/create", {
        vlk_id: state.identityId, purpose: "identity_verification", ttl: 60, one_time: true,
      });
      setQr(r.data);
    } catch (e) {
      toast.error("QR create failed", { description: e?.response?.data?.detail?.code || e.message });
    } finally { setBusy(false); }
  };

  const doVerify = async () => {
    if (!qr) return;
    setBusy(true);
    try {
      const r = await api.post("/qr/verify", { payload: qr.payload, signature: qr.signature });
      setVerify(r.data);
      toast.success("QR verified atomically");
    } catch (e) {
      toast.error("QR verify failed", { description: e?.response?.data?.detail?.code || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="demo-step-qr">
      <div className="text-sm">The institution creates a QR bound to citizen <code className="text-xs bg-muted px-1 py-0.5 rounded">{state.identityId}</code>, then the scanning-side of the same institution verifies it. VeraLok QR is HMAC-signed, single-use (atomic burn), and TTL 60s.</div>

      <div className="grid md:grid-cols-2 gap-3">
        <Button onClick={create} disabled={busy || !!qr} data-testid="demo-qr-create-btn">
          {busy && !qr ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} 1. Create QR
        </Button>
        <Button onClick={doVerify} disabled={busy || !qr || !!verify} data-testid="demo-qr-verify-btn">
          {busy && qr && !verify ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} 2. Verify QR
        </Button>
      </div>

      {qr && (
        <div className="border rounded p-3 text-[11px] font-mono" data-testid="demo-qr-details">
          <div className="text-muted-foreground uppercase tracking-widest text-[9px]">Payload</div>
          <div className="break-all">{qr.payload}</div>
          <div className="text-muted-foreground uppercase tracking-widest text-[9px] mt-2">Signature</div>
          <div className="break-all">{qr.signature.slice(0, 48)}…</div>
        </div>
      )}

      {verify && (
        <div className="border rounded p-3 text-[11px] font-mono bg-green-500/5" data-testid="demo-qr-verified">
          Verified · token_id {verify.token_id} · purpose {verify.purpose}
        </div>
      )}

      <Button onClick={() => onDone({ qr, qrVerify: verify })} disabled={!verify} data-testid="demo-step2-continue">
        Next: Consent request <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

// ─── Step 3: Consent Request ───────────────────────────────────────────────
function Step3Consent({ state, setBusy, busy, onDone }) {
  const [consent, setConsent] = useState(state.consent);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/consent/request", {
        vlk_id: state.identityId,
        vlk_inst_id: state.institutionId,
        scope: ["VERIFY_IDENTITY"],
        purpose: "Guided demo — synthetic identity verification",
      });
      setConsent(r.data);
      toast.success("Consent request sent to citizen");
    } catch (e) {
      toast.error("Consent request failed", { description: e?.response?.data?.detail?.code || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="demo-step-consent">
      <div className="text-sm">
        The institution sends a scoped consent request to the citizen. The citizen must explicitly
        approve or deny — verification cannot proceed without a granted consent.
      </div>
      <Button onClick={create} disabled={busy || !!consent} data-testid="demo-consent-request-btn">
        {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Send consent request
      </Button>
      {consent && (
        <div className="border rounded p-3 text-[11px] font-mono" data-testid="demo-consent-details">
          <div>consent_id: {consent.consent_id}</div>
          <div>state: {consent.state}</div>
          <div>scope: {(consent.scope || []).join(", ")}</div>
        </div>
      )}
      <Button onClick={() => onDone({ consent })} disabled={!consent} data-testid="demo-step3-continue">
        Next: Citizen decision <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

// ─── Step 4: Citizen Approve/Deny ──────────────────────────────────────────
function Step4Decide({ state, setBusy, busy, onDone }) {
  const [decision, setDecision] = useState(state.decision);

  const decide = async (kind) => {
    setBusy(true);
    try {
      // The scaffolding `/consent/{id}/decide` endpoint is deliberately
      // open (see backend `consent.py`) — no citizen re-auth needed
      // inside the demo. The audit row is tagged so it's traceable back
      // to the synthetic decision.
      const r = await api.post(`/consent/${state.consent.consent_id}/decide`, {
        decision: kind,
        reason: `demo-portal synthetic ${kind.toLowerCase()} on behalf of ${DEMO_CITIZEN_EMAIL}`,
      });
      setDecision(r.data);
      toast.success(`Consent ${kind.toLowerCase()}ed by citizen`);
    } catch (e) {
      toast.error("Decision failed", { description: e?.response?.data?.detail?.code || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="demo-step-decide">
      <div className="text-sm">
        Now switching perspective: the citizen sees the request and explicitly grants or denies.
        VeraLok never proceeds without an unambiguous decision.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Button onClick={() => decide("GRANT")} disabled={busy || !!decision}
                data-testid="demo-decide-grant-btn">
          Citizen grants consent
        </Button>
        <Button variant="outline" onClick={() => decide("DENY")} disabled={busy || !!decision}
                data-testid="demo-decide-deny-btn">
          Citizen denies consent
        </Button>
      </div>
      {decision && (
        <div className={`border rounded p-3 text-[11px] font-mono ${
          decision.state === "GRANTED" ? "bg-green-500/5" : "bg-destructive/5"
        }`} data-testid="demo-decision-result">
          Consent {decision.consent_id} state → {decision.state}
        </div>
      )}
      <Button onClick={() => onDone({ decision })} disabled={!decision} data-testid="demo-step4-continue">
        Next: Emit Trust Event <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

// ─── Step 5: Trust Event ───────────────────────────────────────────────────
function Step5TrustEvent({ state, setBusy, busy, onDone }) {
  const [event, setEvent] = useState(state.trustEvent);
  const canEmit = state.decision?.state === "GRANTED";

  const emit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trust-events", {
        vlk_id: state.identityId,
        vlk_inst_id: state.institutionId,
        action: "VERIFY_IDENTITY",
        outcome: "APPROVED",
        consent_ref: state.decision.consent_id,
      });
      setEvent(r.data);
      toast.success("Trust Event committed to chained audit ledger");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const desc = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.type).join("; ")
        : detail?.code || detail || e.message;
      toast.error("Trust Event failed", { description: desc });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="demo-step-trust">
      <div className="text-sm">
        A Trust Event binds one VLK_ID ledger token to one VLK_INST_ID + action/outcome/consent_ref +
        cryptographic fingerprint. The Chained Audit Ledger cryptographically links this to the
        previous entry — tampering breaks the chain.
      </div>
      {!canEmit && (
        <div className="border border-destructive/40 bg-destructive/5 rounded p-3 text-xs">
          Trust Events can only be emitted for GRANTED consents. Restart the demo (Reset button above)
          and choose Grant at Step 4 to see this path.
        </div>
      )}
      <Button onClick={emit} disabled={busy || !canEmit || !!event} data-testid="demo-trust-emit-btn">
        {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Emit Trust Event
      </Button>
      {event && (
        <div className="border rounded p-3 text-[10px] font-mono bg-green-500/5" data-testid="demo-trust-details">
          <div>trust_event_id: {event.trust_event_id}</div>
          <div>ledger_token: {event.vlk_id_ledger_token?.slice(0, 32)}…</div>
          <div>fingerprint: {event.cryptographic_fingerprint?.slice(0, 32)}…</div>
          <div>audit_ref: {event.audit_ref}</div>
        </div>
      )}
      <Button onClick={() => onDone({ trustEvent: event })} disabled={!event} data-testid="demo-step5-continue">
        Next: Audit trail <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

// ─── Step 6: Audit Trail ───────────────────────────────────────────────────
function Step6Audit({ state, setBusy, busy, onDone }) {
  const [rows, setRows] = useState(state.audit || []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/audit?limit=100`);
      const all = r.data?.items || [];
      // Filter by target_id linked to this run
      const relevant = all.filter((a) => {
        const t = a.target_id || "";
        return t === state.qr?.token_id ||
               t === state.consent?.consent_id ||
               t === state.trustEvent?.trust_event_id ||
               a.action?.includes("QR_") ||
               a.action?.includes("CONSENT_") ||
               a.action?.includes("TRUST_");
      }).slice(0, 20);
      setRows(relevant);
    } catch (e) {
      toast.error("Failed to load audit");
    } finally { setBusy(false); }
  }, [state, setBusy]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="demo-step-audit">
      <div className="text-sm">
        Every action taken above wrote an immutable, chained audit row. Below are the entries
        related to this workflow run.
      </div>
      <Button onClick={load} disabled={busy} variant="outline" size="sm" data-testid="demo-audit-refresh">
        <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
      </Button>
      <div className="border rounded overflow-hidden">
        <table className="w-full text-xs" data-testid="demo-audit-table">
          <thead className="bg-muted/30 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-2">Time</th>
              <th className="text-left p-2">Action</th>
              <th className="text-left p-2">Actor</th>
              <th className="text-left p-2">Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">
                No audit rows yet — run the workflow to populate.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.ledger_id} className="border-t">
                <td className="p-2 font-mono">{(r.timestamp || "").slice(11, 19)}</td>
                <td className="p-2 font-mono">{r.action}</td>
                <td className="p-2 truncate max-w-[16ch]">{r.actor_email}</td>
                <td className="p-2 font-mono text-[10px]">{(r.target_id || "").slice(0, 18)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={() => onDone({ audit: rows })} data-testid="demo-step6-continue">
        Next: Analytics <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

// ─── Step 7: Analytics ─────────────────────────────────────────────────────
function Step7Analytics({ state, setBusy, busy }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setBusy(true);
    Promise.all([
      api.get("/analytics/overview?days=7"),
      api.get("/analytics/risk?days=7"),
    ]).then(([o, r]) => {
      setData({ overview: o.data, risk: r.data });
    }).finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3" data-testid="demo-step-analytics">
      <div className="text-sm">
        Every Trust Event flows into VeraLok's analytics — denial rate, risk distribution, compliance posture.
      </div>
      {busy && <div className="text-xs text-muted-foreground">Loading analytics…</div>}
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Verifications (7d)" value={data.overview?.verifications ?? "—"} />
            <Kpi label="Grants (7d)" value={data.overview?.grants ?? "—"} />
            <Kpi label="Denials (7d)" value={data.overview?.denials ?? "—"} />
          </div>
          <details className="border rounded p-3">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-muted-foreground">Raw analytics payload</summary>
            <pre className="text-[10px] font-mono mt-2 max-h-64 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        </>
      )}
      <div className="border rounded p-4 bg-green-500/5" data-testid="demo-complete-card">
        <div className="text-sm font-semibold">Demo complete</div>
        <p className="text-xs text-muted-foreground mt-1">
          You have walked the full VeraLok Trust Verification loop end-to-end. To run again, use the
          Reset button at the top of this page — it wipes the synthetic corpus and re-seeds fresh state.
        </p>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border rounded p-3">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-1 font-mono">{String(value)}</div>
    </div>
  );
}
